import TextFieldsIcon from "@mui/icons-material/TextFields";
import { Box, IconButton, Paper, Tooltip } from "@mui/material";
import { createPortal } from "react-dom";

import { VoiceStyle } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { EMOTION_META } from "../../Utils/spanUtils";

// Must exceed MUI's highest z-index (Popper: 1300, Modal: 1300, Tooltip: 1500)
// so the bubble stays on top even inside modals and over tooltips.
const Z_INDEX_EMOTION_BUBBLE = 1600;

interface EmotionBubbleProps {
  /** Bounding rect of the text selection, used to position the bubble. */
  anchorRect: DOMRect;
  onSelectStyle(style: VoiceStyle | null): void;
  /** Called when the user wants to wrap the selection in a variable. */
  onAddVariable?(): void;
  onClose(): void;
}

const STYLED_EMOTIONS = [
  VoiceStyle.Cheerful,
  VoiceStyle.Excited,
  VoiceStyle.Friendly,
  VoiceStyle.Sad,
  VoiceStyle.Angry,
  VoiceStyle.Calm,
  VoiceStyle.Newscast,
  VoiceStyle.Whispering,
  VoiceStyle.Authoritative,
  VoiceStyle.Empathetic,
] as const;

/**
 * Floating emotion picker that appears above a text selection.
 * Rendered via a portal to document.body to avoid overflow/z-index issues.
 * Uses onMouseDown + preventDefault to preserve the contenteditable selection.
 */
export const EmotionBubble = ({
  anchorRect,
  onSelectStyle,
  onAddVariable,
  onClose,
}: EmotionBubbleProps): React.ReactNode => {
  const { t } = useResource();

  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent blur on the contenteditable when clicking the bubble
    e.preventDefault();
    e.stopPropagation();
  };

  const centerX = anchorRect.left + anchorRect.width / 2;
  const bubbleWidth = onAddVariable ? 350 : 310;
  const left = Math.max(
    8,
    Math.min(centerX - bubbleWidth / 2, window.innerWidth - bubbleWidth - 8),
  );
  const top = anchorRect.top - 52;

  return createPortal(
    <Paper
      elevation={6}
      onMouseDown={handleMouseDown}
      sx={{
        position: "fixed",
        top,
        left,
        width: bubbleWidth,
        zIndex: Z_INDEX_EMOTION_BUBBLE,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: "20px",
        flexWrap: "nowrap",
        overflow: "hidden",
      }}
    >
      {/* Remove emotion chip */}
      <Tooltip title={t(Resources.JobDetail.RemoveEmotion)} placement="top">
        <Box
          onClick={() => {
            onSelectStyle(null);
            onClose();
          }}
          sx={{
            cursor: "pointer",
            fontSize: "14px",
            lineHeight: 1,
            px: 1.5,
            py: 0,
            borderRadius: "12px",
            userSelect: "none",
            flexShrink: 0,
            transition: "transform 100ms, box-shadow 100ms",
            "&:hover": {
              transform: "scale(1.2)",
              boxShadow: "0 0 0 2px rgba(255,255,255,0.2)",
            },
          }}
        >
          ⊘
        </Box>
      </Tooltip>

      {/* One chip per non-neutral style */}
      {STYLED_EMOTIONS.map((style) => {
        const meta = EMOTION_META[style];
        const label = t(
          Resources.Common.VoiceStyles[
            style as keyof typeof Resources.Common.VoiceStyles
          ],
        );
        return (
          <Tooltip key={style} title={label} placement="top">
            <Box
              onClick={() => {
                onSelectStyle(style);
                onClose();
              }}
              sx={{
                cursor: "pointer",
                fontSize: "16px",
                lineHeight: 1,
                px: 0.5,
                py: 0.25,
                borderRadius: "12px",
                backgroundColor: meta.color,
                userSelect: "none",
                flexShrink: 0,
                transition: "transform 100ms, box-shadow 100ms",
                "&:hover": {
                  transform: "scale(1.2)",
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.2)",
                },
              }}
            >
              {meta.emoji}
            </Box>
          </Tooltip>
        );
      })}

      {/* Variable button */}
      {onAddVariable && (
        <>
          <Box
            sx={{
              width: "1px",
              height: "20px",
              bgcolor: "divider",
              flexShrink: 0,
            }}
          />
          <Tooltip title={t(Resources.JobDetail.AddVariable)} placement="top">
            <IconButton
              size="small"
              onClick={() => {
                onAddVariable();
                onClose();
              }}
              sx={{
                py: 0.5,
                px: 1.5,
                flexShrink: 0,
                color: "warning.main",
                "&:hover": { backgroundColor: "rgba(255,152,0,0.12)" },
              }}
            >
              <TextFieldsIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Paper>,
    document.body,
  );
};
